import React, { memo, useCallback, useEffect, useState } from 'react';

import { SelectComponent } from 'atoms/Select/Select';
import { ISelectOptions } from 'context';
import { useOutsideClick } from 'services/hooks/useOutsideClick';

interface ISelectComponent {
  preselectedOption?: ISelectOptions;
  options: ISelectOptions[];
  placeholder: string;
  disabled: boolean;
  onSelect: (option: ISelectOptions) => void;
  className?: string;
}

export const Select = memo(
  ({
    preselectedOption,
    options,
    placeholder,
    disabled,
    onSelect,
    className,
  }: ISelectComponent) => {
    const [isOpened, setIsOpened] = useState<boolean>(false);
    const [selectedOption, setSelectedOption] = useState<ISelectOptions>({ label: '', value: '' });

    useEffect(() => {
      if (preselectedOption) {
        setSelectedOption(preselectedOption);
      }
    }, [preselectedOption]);

    const handleClickOutside = useCallback(() => setIsOpened(false), []);

    const ref = useOutsideClick(handleClickOutside);

    const onSelectClick = useCallback(() => setIsOpened((prevState) => !prevState), []);

    const onOptionClick = useCallback(
      (option: ISelectOptions) => {
        setSelectedOption({ label: option.label, value: option.value });
        onSelect(option);
      },
      [onSelect],
    );

    return (
      <SelectComponent
        options={options}
        placeholder={placeholder}
        selectedOption={selectedOption}
        isOpened={isOpened}
        onOptionClick={onOptionClick}
        onSelectClick={onSelectClick}
        dropdownRef={ref}
        disabled={disabled}
        className={className}
      />
    );
  },
);
